import type { LeadUploadEvent } from "./types";

/* ══════════════════════════════════════════════════════════════════════════
   lead_upload — pick a CSV and PUT it straight into the bucket.

   The assistant sends an S3 presigned POST. Three things about that shape are
   easy to get wrong and all three fail the same way, with an opaque 403 from
   S3 rather than an error the app can explain:

   1. `fields` must be appended BEFORE the file. S3 parses the multipart body
      in order and ignores every field that appears after the file part, so a
      policy sent last is a policy that was never sent.

   2. The file part must be named `file`. That name is part of the signed
      policy, not a convention we choose.

   3. The request must carry NO Authorization header. This deliberately does
      not use `core/axios`, which attaches the Cognito ID token to everything:
      an Authorization header on a presigned POST invalidates the signature.
      The URL is the credential here.

   The browser also has to set the multipart boundary itself, so Content-Type
   is never set by hand.
   ══════════════════════════════════════════════════════════════════════════ */

export interface LeadUploadResult {
    status: "uploaded" | "cancelled" | "rejected" | "failed";
    customerGroupId: number;
    fileName?: string;
    /** Present when status is "rejected" or "failed" */
    reason?: string;
}

const CSV_ACCEPT = ".csv,text/csv,application/csv,text/comma-separated-values";

/** The `accept` attribute is a filter, not a guarantee — drag-and-drop and
 *  "All Files" both get past it, so the choice is checked again here. */
export function isCsvFile(file: File): boolean {
    if (/\.csv$/i.test(file.name)) return true;
    return ["text/csv", "application/csv", "text/comma-separated-values"].includes(file.type);
}

/**
 * Opens the OS file picker and resolves with the chosen file, or null if the
 * dialog was dismissed.
 *
 * Cancellation has no reliable event: `change` never fires. `cancel` is
 * supported in current browsers and a focus fallback covers the rest, so the
 * promise always settles instead of leaking a pending upload.
 */
export function pickCsvFile(): Promise<File | null> {
    return new Promise((resolve) => {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = CSV_ACCEPT;
        input.multiple = false;
        input.style.display = "none";
        document.body.appendChild(input);

        let settled = false;
        const finish = (file: File | null) => {
            if (settled) return;
            settled = true;
            window.removeEventListener("focus", onFocus);
            input.remove();
            resolve(file);
        };

        const onFocus = () => {
            // Fires when the dialog closes. A `change` for a real selection
            // lands first, so a still-empty input here means "dismissed".
            window.setTimeout(() => {
                if (!input.files?.length) finish(null);
            }, 300);
        };

        input.addEventListener("change", () => finish(input.files?.[0] ?? null));
        input.addEventListener("cancel", () => finish(null));
        window.addEventListener("focus", onFocus);

        input.click();
    });
}

/** Builds the multipart body S3 expects. Exported so the ordering is testable. */
export function buildUploadForm(
    fields: Record<string, string>,
    file: File
): FormData {
    const form = new FormData();
    // Policy and signature first — see (1) above.
    for (const [key, value] of Object.entries(fields ?? {})) {
        form.append(key, value);
    }
    // ...and the file last, under the name the policy signed.
    form.append("file", file, file.name);
    return form;
}

/**
 * Runs the whole exchange: pick a CSV, then post it to the presigned target.
 *
 * Resolves rather than throws, because this is triggered by a socket frame and
 * there is no call site holding a try/catch — the caller turns the result into
 * a message in the conversation.
 */
export async function runLeadUpload(
    event: LeadUploadEvent,
    deps: { pickFile?: typeof pickCsvFile; post?: typeof postToPresignedTarget } = {}
): Promise<LeadUploadResult> {
    const pick = deps.pickFile ?? pickCsvFile;
    const post = deps.post ?? postToPresignedTarget;
    const customerGroupId = event.customer_group_id;

    const file = await pick();
    if (!file) return { status: "cancelled", customerGroupId };

    if (!isCsvFile(file)) {
        return {
            status: "rejected",
            customerGroupId,
            fileName: file.name,
            reason: "Only CSV files can be uploaded.",
        };
    }

    try {
        await post(event.upload_target, file);
        return { status: "uploaded", customerGroupId, fileName: file.name };
    } catch (err: any) {
        return {
            status: "failed",
            customerGroupId,
            fileName: file.name,
            reason: err?.message ?? "The upload did not complete.",
        };
    }
}

/** POSTs the multipart body. Throws on a non-2xx so callers can report it. */
export async function postToPresignedTarget(
    target: LeadUploadEvent["upload_target"],
    file: File
): Promise<void> {
    const response = await fetch(target.url, {
        method: "POST",
        body: buildUploadForm(target.fields, file),
        // No headers at all: see (3). Content-Type is the browser's to set.
    });

    if (!response.ok) {
        throw new Error(
            `Upload failed (${response.status}). The link may have expired — ask the assistant to try again.`
        );
    }
}
